import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  BadgeDollarSign,
  BarChart3,
  CheckCircle2,
  CircleDollarSign,
  Eye,
  Gauge,
  MousePointerClick,
  Percent,
  Receipt,
  Store,
  Target,
  type LucideIcon,
} from "lucide-react";
import { BarChart, type BarChartDataset } from "../components/BarChart";
import {
  RESERS_BRANDS,
  RESERS_METRIC_COLORS,
  RESERS_RETAILERS,
  aggregateMetrics,
  getBrand,
  getBrandMonthlyTotals,
  getBrandRecords,
  getBrandRetailerRecords,
  getBrandTotals,
  getRetailerMixForBrand,
  getRetailersForBrand,
  type ResersBrandId,
  type ResersMetricTotals,
  type ResersMonthlyMetric,
} from "./resersQ1Data";

const formatMoney = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatPreciseMoney = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: value < 10 ? 2 : 0,
    maximumFractionDigits: value < 10 ? 2 : 0,
  });

const formatCompactMoney = (value: number) => {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (absValue >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return formatPreciseMoney(value);
};

const formatNumber = (value: number) =>
  Math.round(value).toLocaleString("en-US");

const formatCompactNumber = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

const formatPercent = (value: number, precision = 1) =>
  `${(value * 100).toFixed(precision)}%`;

const formatRoas = (value: number) => `${value.toFixed(2)}x`;

const formatMonth = (month: string) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
  });

const formatDate = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const formatDateRange = (start: string, end: string) => {
  if (!start || !end) return "Q1 2026";
  if (start === end) return formatDate(start);
  return `${formatDate(start)}-${formatDate(end)}, 2026`;
};

interface MetricTileConfig {
  label: string;
  value: string;
  detail: string;
  icon: LucideIcon;
}

const metricGridItems = (totals: ResersMetricTotals): MetricTileConfig[] => [
  {
    label: "Impressions",
    value: formatCompactNumber(totals.impressions),
    detail: formatNumber(totals.impressions),
    icon: Eye,
  },
  {
    label: "Clicks",
    value: formatCompactNumber(totals.clicks),
    detail: formatNumber(totals.clicks),
    icon: MousePointerClick,
  },
  {
    label: "CTR",
    value: formatPercent(totals.ctr, 2),
    detail: "clicks / impressions",
    icon: Percent,
  },
  {
    label: totals.conversionLabel,
    value: formatCompactNumber(totals.conversionValue),
    detail:
      totals.conversionMetric === "conversions"
        ? "reported conversions"
        : "conversion proxy",
    icon: CheckCircle2,
  },
  {
    label: totals.conversionRateLabel,
    value: formatPercent(totals.conversionRate, 1),
    detail:
      totals.conversionMetric === "conversions"
        ? "conversions / clicks"
        : "units / clicks",
    icon: Target,
  },
  {
    label: totals.costPerConversionLabel,
    value: formatPreciseMoney(totals.costPerConversion),
    detail:
      totals.conversionMetric === "conversions"
        ? "spend / conversions"
        : "spend / units",
    icon: BadgeDollarSign,
  },
  {
    label: "ROAS",
    value: formatRoas(totals.roas),
    detail: "sales / spend",
    icon: Gauge,
  },
  {
    label: "Cost / Click",
    value: formatPreciseMoney(totals.cpc),
    detail: "spend / clicks",
    icon: CircleDollarSign,
  },
  {
    label: "Sales",
    value: formatCompactMoney(totals.sales),
    detail: formatMoney(totals.sales),
    icon: Receipt,
  },
];

interface MetricGridProps {
  totals: ResersMetricTotals;
  accentColor: string;
  title?: string;
  tileHeightClass?: string;
  className?: string;
  gridClassName?: string;
  metricBodyClassName?: string;
  labelClassName?: string;
  iconClassName?: string;
  valueClassName?: string;
  detailClassName?: string;
}

const MetricGrid: React.FC<MetricGridProps> = ({
  totals,
  accentColor,
  title,
  tileHeightClass = "min-h-[126px]",
  className = "",
  gridClassName = "",
  metricBodyClassName = "mt-3",
  labelClassName = "text-xs",
  iconClassName = "h-4 w-4",
  valueClassName = "text-2xl",
  detailClassName = "mt-2 text-xs",
}) => (
  <div className={className}>
    {title && (
      <h3 className="mb-3 text-base font-semibold text-slate-950 dark:text-stone-50">
        {title}
      </h3>
    )}
    <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${gridClassName}`}>
      {metricGridItems(totals).map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.label}
            className={`${tileHeightClass} flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-[#5B5353] dark:bg-[#403838]`}
          >
            <div className="flex items-start justify-between gap-2">
              <span
                className={`${labelClassName} font-semibold leading-snug text-slate-600 dark:text-[#B5A9AA]`}
              >
                {item.label}
              </span>
              <Icon
                className={`${iconClassName} shrink-0`}
                style={{ color: accentColor }}
                aria-hidden="true"
              />
            </div>
            <div className={metricBodyClassName}>
              <div
                className={`${valueClassName} break-words font-semibold leading-tight text-slate-950 dark:text-[#FFF8F9]`}
              >
                {item.value}
              </div>
              <div
                className={`${detailClassName} break-words leading-snug text-slate-500 dark:text-[#B5A9AA]`}
              >
                {item.detail}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

interface ChartPanelProps {
  title: string;
  subtitle: string;
  labels: string[];
  datasets: BarChartDataset[];
  valueFormatter?: (value: number) => string;
  height?: string;
  showLegend?: boolean;
  horizontal?: boolean;
}

const ChartPanel: React.FC<ChartPanelProps> = ({
  title,
  subtitle,
  labels,
  datasets,
  valueFormatter = formatCompactMoney,
  height = "h-72",
  showLegend = true,
  horizontal = false,
}) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-[#5B5353] dark:bg-[#403838]">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h3 className="text-base font-semibold text-slate-950 dark:text-stone-50">
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-[#B5A9AA]">
          {subtitle}
        </p>
      </div>
      <BarChart3 className="h-5 w-5 text-slate-400" aria-hidden="true" />
    </div>
    <BarChart
      labels={labels}
      datasets={datasets}
      height={height}
      horizontal={horizontal}
      showLegend={showLegend}
      valueFormatter={valueFormatter}
    />
  </div>
);

interface SpendSalesChartProps {
  records: ResersMonthlyMetric[];
  title: string;
  subtitle: string;
  salesColor: string;
  spendColor: string;
  height?: string;
}

const SpendSalesChart: React.FC<SpendSalesChartProps> = ({
  records,
  title,
  subtitle,
  salesColor,
  spendColor,
  height,
}) => {
  const orderedRecords = [...records].sort((a, b) =>
    a.month.localeCompare(b.month),
  );

  return (
    <ChartPanel
      title={title}
      subtitle={subtitle}
      labels={orderedRecords.map((record) => formatMonth(record.month))}
      height={height}
      datasets={[
        {
          label: "Sales",
          data: orderedRecords.map((record) => record.sales),
          backgroundColor: salesColor,
          valueFormatter: formatMoney,
          dataLabelFormatter: formatCompactMoney,
        },
        {
          label: "Spend",
          data: orderedRecords.map((record) => record.spend),
          backgroundColor: spendColor,
          valueFormatter: formatMoney,
          dataLabelFormatter: formatCompactMoney,
        },
      ]}
    />
  );
};

interface RetailerMixPanelProps {
  brandId: ResersBrandId;
}

const RetailerMixPanel: React.FC<RetailerMixPanelProps> = ({ brandId }) => {
  const mix = getRetailerMixForBrand(brandId);
  const maxSales = Math.max(1, ...mix.map((item) => item.sales));

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-[#5B5353] dark:bg-[#403838]">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-950 dark:text-stone-50">
            Sales Mix by Retailer
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-[#B5A9AA]">
            Retailer share of total brand sales with unit share included.
          </p>
        </div>
        <Store className="h-5 w-5 text-slate-400" aria-hidden="true" />
      </div>
      <div className="space-y-4">
        {mix.map((item) => (
          <div key={item.retailerId}>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900 dark:text-stone-50">
                  {item.retailerName}
                </div>
                <div className="text-xs text-slate-500 dark:text-[#B5A9AA]">
                  {formatNumber(item.units)} units | {formatRoas(item.roas)} ROAS
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-950 dark:text-stone-50">
                  {formatCompactMoney(item.sales)}
                </div>
                <div className="text-xs text-slate-500 dark:text-[#B5A9AA]">
                  {formatPercent(item.percentageOfSales, 1)} of sales
                </div>
              </div>
            </div>
            <div className="h-3 overflow-hidden rounded-lg bg-slate-100 dark:bg-[#2C2525]">
              <div
                className="h-full rounded-lg"
                style={{
                  width: `${Math.max(2, (item.sales / maxSales) * 100)}%`,
                  backgroundColor: item.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TakeawaysBox: React.FC<{ retailerName: string }> = ({ retailerName }) => (
  <div className="min-h-[148px] rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-[#5B5353] dark:bg-[#403838]">
    <h4 className="text-base font-semibold text-slate-950 dark:text-stone-50">
      Key Takeaways
    </h4>
    <div
      className="mt-4 min-h-[76px] rounded-lg border border-dashed border-slate-200 bg-slate-50 dark:border-[#5B5353] dark:bg-[#2C2525]"
      aria-label={`${retailerName} key takeaways notes area`}
    />
  </div>
);

interface RetailerDetailSectionProps {
  brandId: ResersBrandId;
  retailerId: ResersMonthlyMetric["retailerId"];
}

const RetailerDetailSection: React.FC<RetailerDetailSectionProps> = ({
  brandId,
  retailerId,
}) => {
  const brand = getBrand(brandId);
  const records = getBrandRetailerRecords(brandId, retailerId);
  const totals = aggregateMetrics(records);
  const retailer =
    RESERS_RETAILERS.find((item) => item.id === retailerId) ?? RESERS_RETAILERS[0];

  return (
    <section className="border-t border-slate-200 pt-8 dark:border-[#5B5353]">
      <div className="grid items-stretch gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="space-y-5">
          <div>
            <div
              className="mb-2 h-1 w-16 rounded-lg"
              style={{ backgroundColor: retailer.color }}
            />
            <div className="flex flex-wrap items-center gap-4">
              <h3 className="text-2xl font-semibold text-slate-950 dark:text-stone-50">
                {brand.name} at {retailer.name}
              </h3>
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-left shadow-sm dark:border-[#5B5353] dark:bg-[#403838]">
                <div className="text-xs font-semibold text-slate-500 dark:text-[#B5A9AA]">
                  Retailer ROAS
                </div>
                <div className="text-2xl font-semibold text-slate-950 dark:text-stone-50">
                  {formatRoas(totals.roas)}
                </div>
              </div>
            </div>
            <p className="mt-1 text-sm text-slate-500 dark:text-[#B5A9AA]">
              {formatDateRange(totals.dateRangeStart, totals.dateRangeEnd)} |{" "}
              {totals.sourceFiles.join(", ")}
            </p>
          </div>
          <SpendSalesChart
            records={records}
            title="Spend vs Sales by Month"
            subtitle={`${retailer.name} monthly performance`}
            salesColor={RESERS_METRIC_COLORS.sales}
            spendColor={RESERS_METRIC_COLORS.spend}
            height="h-52"
          />
          <TakeawaysBox retailerName={retailer.name} />
        </div>
        <MetricGrid
          totals={totals}
          accentColor={retailer.color}
          title={`${retailer.shortName} Metric Grid`}
          tileHeightClass="min-h-[148px] xl:min-h-0"
          className="xl:flex xl:h-full xl:flex-col"
          gridClassName="xl:flex-1 xl:grid-rows-3"
          metricBodyClassName="flex flex-1 flex-col justify-center pt-4"
          labelClassName="text-sm"
          iconClassName="h-5 w-5"
          valueClassName="text-3xl"
          detailClassName="mt-3 text-sm"
        />
      </div>
    </section>
  );
};

interface BrandOverviewProps {
  brandId: ResersBrandId;
}

const BrandOverview: React.FC<BrandOverviewProps> = ({ brandId }) => {
  const brand = getBrand(brandId);
  const records = getBrandRecords(brandId);
  const totals = getBrandTotals(brandId);
  const monthlyTotals = getBrandMonthlyTotals(brandId);
  const retailers = getRetailersForBrand(brandId);

  const roasLabels = retailers.map((retailer) => retailer.shortName);
  const roasDatasets: BarChartDataset[] = [
    {
      label: "ROAS",
      data: retailers.map((retailer) =>
        aggregateMetrics(getBrandRetailerRecords(brandId, retailer.id)).roas,
      ),
      backgroundColor: retailers.map((retailer) => retailer.color),
      valueFormatter: formatRoas,
      dataLabelFormatter: formatRoas,
    },
  ];

  return (
    <div className="space-y-10">
      <section>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div
              className="mb-3 h-1.5 w-24 rounded-lg"
              style={{ backgroundColor: brand.color }}
            />
            <h2 className="text-3xl font-semibold text-slate-950 dark:text-stone-50">
              {brand.name} Q1 Retailer Performance
            </h2>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-[#B5A9AA]">
              {formatDateRange(totals.dateRangeStart, totals.dateRangeEnd)} |{" "}
              {retailers.map((retailer) => retailer.name).join(", ")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-right">
            <div>
              <div className="text-xs font-semibold text-slate-500 dark:text-[#B5A9AA]">
                Sales
              </div>
              <div className="text-2xl font-semibold text-slate-950 dark:text-stone-50">
                {formatCompactMoney(totals.sales)}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 dark:text-[#B5A9AA]">
                Spend
              </div>
              <div className="text-2xl font-semibold text-slate-950 dark:text-stone-50">
                {formatCompactMoney(totals.spend)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-2">
        <MetricGrid
          totals={totals}
          accentColor={brand.color}
          title="Overview Metric Grid"
          tileHeightClass="min-h-[118px]"
        />
        <ChartPanel
          title="ROAS by Retailer"
          subtitle="Efficiency comparison across active retailers"
          labels={roasLabels}
          datasets={roasDatasets}
          valueFormatter={formatRoas}
          horizontal
          showLegend={false}
          height="h-64"
        />
        <SpendSalesChart
          records={monthlyTotals.map(({ month, totals: monthTotals }) => ({
            brandId,
            brandName: brand.name,
            retailerId: records[0]?.retailerId ?? "kroger",
            retailerName: "All retailers",
            month,
            impressions: monthTotals.impressions,
            clicks: monthTotals.clicks,
            spend: monthTotals.spend,
            sales: monthTotals.sales,
            units: monthTotals.units,
            conversions: monthTotals.conversions,
            conversionMetric: monthTotals.conversionMetric,
            sourceFiles: monthTotals.sourceFiles,
            dateRangeStart: monthTotals.dateRangeStart,
            dateRangeEnd: monthTotals.dateRangeEnd,
          }))}
          title="Brand Spend vs Sales by Month"
          subtitle="All reporting retailers combined"
          salesColor={RESERS_METRIC_COLORS.sales}
          spendColor={RESERS_METRIC_COLORS.spend}
        />
        <RetailerMixPanel brandId={brandId} />
      </section>

      <section className="space-y-8">
        <div>
          <h2 className="text-2xl font-semibold text-slate-950 dark:text-stone-50">
            Retailer Detail
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-[#B5A9AA]">
            Each retailer keeps its own upload boundary; brand metrics are shown
            separately inside Kroger and ABSCO.
          </p>
        </div>
        {retailers.map((retailer) => (
          <RetailerDetailSection
            key={`${brandId}-${retailer.id}`}
            brandId={brandId}
            retailerId={retailer.id}
          />
        ))}
      </section>
    </div>
  );
};

export const ResersQ1DashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedBrand = searchParams.get("brand");
  const selectedBrandId: ResersBrandId =
    requestedBrand === "smk" || requestedBrand === "msb"
      ? requestedBrand
      : "msb";

  const handleBrandChange = (brandId: ResersBrandId) => {
    setSearchParams({ brand: brandId });
  };

  return (
    <div
      className="min-h-screen bg-slate-50 px-4 py-8 text-slate-950 dark:bg-[#2C2525] dark:text-[#FFF8F9] sm:px-6 lg:pl-8 lg:pr-14 xl:pr-16"
      data-dashboard
    >
      <div className="mx-auto max-w-[1480px] space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm dark:border-[#5B5353] dark:bg-[#403838] dark:text-[#FFF8F9]">
              <Receipt className="h-4 w-4" aria-hidden="true" />
              Reser's Brands Reporting
            </div>
            <h1 className="text-4xl font-semibold text-slate-950 dark:text-stone-50">
              Q1 2026 Brand Performance Dashboard
            </h1>
            <p className="mt-3 max-w-4xl text-base text-slate-600 dark:text-[#B5A9AA]">
              Quarterly reporting views for Main St Bistro and Stonemill
              Kitchens across AHOLD, Kroger, Albertsons, and Stater Bros.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-[#5B5353] dark:bg-[#403838]">
            {RESERS_BRANDS.map((brand) => {
              const isActive = brand.id === selectedBrandId;
              return (
                <button
                  key={brand.id}
                  type="button"
                  onClick={() => handleBrandChange(brand.id)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-white"
                      : "text-slate-600 hover:bg-slate-100 dark:text-[#B5A9AA] dark:hover:bg-[#5B5353]"
                  }`}
                  style={{
                    backgroundColor: isActive ? brand.color : "transparent",
                  }}
                >
                  {brand.shortName}
                </button>
              );
            })}
          </div>
        </header>

        <BrandOverview brandId={selectedBrandId} />
      </div>
    </div>
  );
};
